import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Divider,
  Button,
  Stack,
  IconButton,
  Box,
  Paper,
  useMediaQuery,
  useTheme,
  ToggleButton,
  Collapse,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  reflectDayQuestions,
  reflectRatingScale,
} from '../../constants/checkinQuestions';

const ReflectYourDayDialog = ({ open, onClose, values, onChange, onComplete, onAfterComplete, submitting = false, isCompleted = false }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const questions = reflectDayQuestions;

  const handleScaleChange = (questionId, value) => {
    onChange({
      ...values,
      [questionId]: value,
    });
  };

  const handleComplete = async () => {
    try {
      if (onComplete) {
        await onComplete();
      }
      onAfterComplete?.();
      onClose();
    } catch (e) {
      // onComplete should toast / handle errors; keep dialog open
    }
  };

  const handleDialogClose = () => {
    onClose();
  };

  const isComplete = questions.every((q) => values[q.id]);

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      aria-labelledby="reflect-day-title"
      aria-describedby="reflect-day-description"
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          maxHeight: fullScreen ? "100vh" : "90vh",
        },
      }}
    >
      <DialogTitle id="reflect-day-title" sx={{ pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            Nightly Reflection
          </Typography>
          <IconButton onClick={handleDialogClose} sx={{ color: "grey.500" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3} id="reflect-day-description">
            <Box>
              <Typography variant="h6" fontWeight="medium" gutterBottom>
                Day Done? 3 Minutes to Reflect, Score, and Grow.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Your Three-minute reflection of Daily Balance and Well-being is
                below.
              </Typography>
            </Box>

            <Divider />

            {questions.map((question, index) => (
              <Box key={question.id}>
                <Typography
                  variant="subtitle1"
                  fontWeight="medium"
                  gutterBottom
                >
                  {index + 1}. {question.prompt}
                </Typography>

                {/* Flat grid with equal-width boxes */}
                <Box
                  sx={{
                    my: 2,
                    width: "100%",
                    display: "grid",
                    gap: 1,
                    gridTemplateColumns: {
                      xs: "repeat(2, 1fr)",
                      sm: "repeat(5, 1fr)",
                    },
                  }}
                >
                  {reflectRatingScale.map((rating) => (
                    <ToggleButton
                      key={rating.value}
                      value={String(rating.value)}
                      selected={
                        values[question.id] === String(rating.value)
                      }
                      onChange={() =>
                        handleScaleChange(
                          question.id,
                          String(rating.value)
                        )
                      }
                      sx={{
                        width: "100%",
                        height: 90,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        gap: 0.5,
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 2,
                        textTransform: "none",

                        "&.Mui-selected": {
                          backgroundColor: "primary.main",
                          color: "white",
                          borderColor: "primary.main",
                          "&:hover": {
                            backgroundColor: "primary.dark",
                          },
                        },
                      }}
                    >
                      <Typography variant="h5">
                        {rating.emoji}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "0.7rem" }}
                      >
                        {rating.label}
                      </Typography>
                    </ToggleButton>
                  ))}
                </Box>
              </Box>
            ))}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleDialogClose} variant="outlined">
          Close
        </Button>
        <Button
          onClick={handleComplete}
          variant="contained"
          color="primary"
          disabled={!isComplete || submitting}
        >
          {submitting ? "Saving…" : (isCompleted ? "Re-submit" : "Complete")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReflectYourDayDialog;
